import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SeaScene } from "./SeaScene";

export const Hero = ({ t, onSignup }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden border-b-[3px] border-[#0a0a0a]">
      <SeaScene />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0a0a0a]/80 to-transparent" />

      <motion.div style={{ opacity, y: yText }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-40 sm:px-6 lg:px-8">
        <span className="mask-line">
          <motion.span
            data-testid="hero-kicker"
            className="toon-chip mb-7 inline-flex items-center gap-3 bg-[#f4f1ea] px-5 py-2.5 font-mono2 text-[11px] font-bold tracking-[0.3em] text-[#0a0a0a]"
            initial={{ y: "130%" }}
            animate={{ y: 0 }}
            transition={{ delay: 0.5, duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#d96b43]" />
            {t.hero.kicker}
          </motion.span>
        </span>

        <h1 className="font-script uppercase leading-[0.95]">
          <span className="mask-line">
            <motion.span
              data-testid="hero-line-0"
              className="toon-text block text-[13.5vw] text-[#f4f1ea] sm:text-[10vw] lg:text-[8.5vw]"
              initial={{ y: "112%", rotate: 3 }}
              animate={{ y: 0, rotate: -1 }}
              transition={{ delay: 0.65, duration: 0.85, ease: [0.34, 1.56, 0.64, 1] }}
            >
              {t.hero.lines[0]} {t.hero.lines[1]}
            </motion.span>
          </span>
          <span className="mask-line">
            <motion.span
              data-testid="hero-line-1"
              className="toon-text block text-[13.5vw] text-[#e5b83b] sm:text-[10vw] lg:text-[8.5vw]"
              initial={{ y: "112%", rotate: -3 }}
              animate={{ y: 0, rotate: 1 }}
              transition={{ delay: 0.8, duration: 0.85, ease: [0.34, 1.56, 0.64, 1] }}
            >
              {t.hero.lines[2]} {t.hero.lines[3]}
            </motion.span>
          </span>
        </h1>

        <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <motion.p
            data-testid="hero-sub"
            className="max-w-md border-l-4 border-[#e5b83b] bg-[#0a0a0a]/55 p-4 text-base leading-relaxed text-[#f4f1ea] backdrop-blur-sm sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.7 }}
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            className="flex flex-col items-start gap-4 sm:items-end"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.7 }}
          >
            <span data-testid="hero-date" className="toon-chip bg-[#d96b43] px-4 py-1.5 font-mono2 text-[11px] font-bold tracking-[0.3em] text-[#0a0a0a]">
              {t.hero.date}
            </span>
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="hero-signup-button"
                onClick={onSignup}
                className="btn-toon bg-[#d96b43] px-8 py-4 text-xl text-[#0a0a0a]"
              >
                {t.hero.cta1}
              </button>
              <a
                data-testid="hero-collection-link"
                href="#drop"
                className="btn-toon bg-[#f4f1ea] px-8 py-4 text-xl text-[#0a0a0a]"
              >
                {t.hero.cta2}
              </a>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
